import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { useAuth } from "@/hooks/useAuth";
import RazorpayCheckout from "@/components/checkout/RazorpayCheckout";

const Pricing = () => {
  const { user, profile } = useAuth();
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: "Extract",
      tier: "free",
      description: "Try the extraction",
      price: { monthly: 0, annual: 0 },
      credits: 3,
      features: [
        "3 transformations/month",
        "All 6 output formats",
        "Unlimited revisions",
        "Email support",
      ],
      popular: false,
      ctaText: "Start Extracting",
      isFree: true,
    },
    {
      name: "Deploy",
      tier: "starter",
      description: "For solo marketers",
      price: { monthly: 2499, annual: 24990 },
      credits: 50,
      features: [
        "50 transformations/month",
        "Priority processing",
        "Brand voice training",
        "Custom tone settings",
        "Export to PDF & Canva",
        "Analytics dashboard",
      ],
      popular: true,
      ctaText: "Start Deploying",
      isFree: false,
    },
    {
      name: "Scale",
      tier: "pro",
      description: "For agencies & teams",
      price: { monthly: 6499, annual: 64990 },
      credits: -1,
      features: [
        "Unlimited transformations",
        "3 team seats",
        "API access",
        "Custom templates",
        "Multi-brand support",
        "Priority support",
        "White-label options",
      ],
      popular: false,
      ctaText: "Start Scaling",
      isFree: false,
    },
    {
      name: "Enterprise",
      tier: "agency",
      description: "Custom solutions",
      price: { monthly: 16499, annual: 164990 },
      credits: -1,
      features: [
        "Everything in Scale",
        "Unlimited team seats",
        "Custom integrations",
        "Dedicated success manager",
        "SLA guarantee",
        "Custom contract terms",
        "On-premise option",
      ],
      popular: false,
      ctaText: "Contact Sales",
      isFree: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-editorial text-4xl md:text-5xl text-foreground mb-4">
            Simple Extraction Pricing
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            No hidden fees. No credit expiry. Upgrade or cancel anytime.
          </p>
          <div className="flex items-center justify-center space-x-4 mb-8">
            <span className={`text-sm ${!isAnnual ? "text-foreground font-medium" : "text-muted-foreground"}`}>Monthly</span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} className="data-[state=checked]:bg-brand-600" />
            <span className={`text-sm ${isAnnual ? "text-foreground font-medium" : "text-muted-foreground"}`}>Annual</span>
            {isAnnual && (
              <Badge className="bg-success/10 text-success border-success/20 ml-2">Save 17%</Badge>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {plans.map((plan, index) => {
            const price = isAnnual ? plan.price.annual : plan.price.monthly;
            const displayPrice = isAnnual ? Math.round(price / 12) : price;
            const isCurrentPlan = profile?.plan === plan.tier;

            return (
              <Card
                key={index}
                className={`relative border-2 transition-all duration-300 hover:shadow-lg ${
                  plan.popular ? "border-brand-500 shadow-lg shadow-brand-500/10" : "border-border hover:border-brand-200"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="gradient-brand text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="font-editorial text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                  <div className="mt-4">
                    <div className="text-4xl font-bold text-foreground">
                      {price === 0 ? "Free" : `₹${displayPrice.toLocaleString("en-IN")}`}
                      {price > 0 && <span className="text-lg font-normal text-muted-foreground">/{isAnnual ? "mo" : "month"}</span>}
                    </div>
                    {isAnnual && price > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">Billed annually (₹{price.toLocaleString("en-IN")})</p>
                    )}
                    <p className="text-sm text-brand-600 font-medium mt-2">
                      {plan.credits === -1 ? "Unlimited" : plan.credits} transformations
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start">
                        <Check className="h-4 w-4 text-brand-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrentPlan ? (
                    <Button className="w-full" disabled variant="outline">Current Plan</Button>
                  ) : plan.isFree ? (
                    <Link to={user ? "/agent" : "/auth"} className="block">
                      <Button className="w-full" variant="outline">{plan.ctaText}</Button>
                    </Link>
                  ) : plan.name === "Enterprise" ? (
                    <Button className="w-full" variant="outline" onClick={() => (window.location.href = "mailto:hello@praise.app")}>
                      {plan.ctaText}
                    </Button>
                  ) : (
                    <RazorpayCheckout
                      planName={plan.tier}
                      isAnnual={isAnnual}
                      className={`w-full ${plan.popular ? "gradient-brand text-white" : ""}`}
                      variant={plan.popular ? "default" : "outline"}
                    >
                      {plan.ctaText}
                    </RazorpayCheckout>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="font-editorial text-2xl text-center text-foreground mb-8">Questions & Answers</h2>
          <div className="space-y-4">
            {[
              { q: "How do transformations work?", a: "Paste any testimonial. Get 6 platform-ready formats in 60 seconds. Each transformation counts as 1 credit." },
              { q: "What if I don't like the output?", a: "Every plan includes unlimited revisions. Adjust tone, length, or style until it matches your voice." },
              { q: "Is my data secure?", a: "Testimonials are processed and discarded. We don't train on your content. Your praise stays yours." },
              { q: "Can I cancel anytime?", a: "Instantly, from your dashboard. No calls, no retention team, no friction." },
              { q: "What payment methods do you accept?", a: "We use Razorpay — supporting UPI, credit/debit cards, net banking, and wallets." },
            ].map((faq, i) => (
              <Card key={i} className="border-border">
                <CardContent className="p-6">
                  <h3 className="font-medium text-foreground mb-2">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-4">Your praise is dormant. Your competitors are deploying.</p>
          <Link to={user ? "/agent" : "/auth"}>
            <Button size="lg" className="gradient-brand text-white text-lg px-8 py-4 shadow-brand">Start Extracting</Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-4">3 free transformations. No credit card required.</p>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default Pricing;
